import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Store, Package, GraduationCap, Search, Crown, SlidersHorizontal } from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import { useSeo } from "@/lib/seo";

const FILTERS = [
  { key: "all", label: "Toutes" },
  { key: "premium", label: "Premium" },
  { key: "products", label: "Avec produits" },
  { key: "formations", label: "Avec formations" },
];

export default function Boutiques() {
  const [shops, setShops] = useState(undefined);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  useSeo({ title: "Boutiques des revendeurs — ISMO-TECH" });

  useEffect(() => {
    api.get("/shops").then(({ data }) => setShops(data)).catch(() => setShops([]));
  }, []);

  const filtered = useMemo(() => {
    if (!shops) return shops;
    const ql = q.toLowerCase().trim();
    return shops.filter((s) => {
      if (ql && !`${s.shop_name} ${s.shop_description || ""}`.toLowerCase().includes(ql)) return false;
      if (filter === "premium" && !s.is_premium) return false;
      if (filter === "products" && !s.products) return false;
      if (filter === "formations" && !s.formations) return false;
      return true;
    });
  }, [shops, q, filter]);

  return (
    <div className="container-it py-10" data-testid="boutiques-page">
      <div className="mb-6">
        <p className="label-eyebrow">Marketplace</p>
        <h1 className="mt-1 font-display text-3xl font-extrabold text-ink">Nos boutiques revendeurs</h1>
        <p className="mt-2 text-sm text-slate-500">Découvrez les boutiques indépendantes et leurs produits & formations.</p>
      </div>

      {/* Recherche + filtres */}
      <div className="mb-8 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="relative w-full lg:max-w-md">
          <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            data-testid="boutiques-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Rechercher une boutique…"
            className="w-full rounded-full border border-line bg-white py-2.5 pl-10 pr-4 text-sm outline-none focus:border-brand"
          />
        </div>
        <div className="flex items-center gap-1.5 overflow-x-auto no-scrollbar">
          <SlidersHorizontal className="mr-1 h-4 w-4 shrink-0 text-slate-400" />
          {FILTERS.map((f) => (
            <button
              key={f.key}
              data-testid={`boutiques-filter-${f.key}`}
              onClick={() => setFilter(f.key)}
              className={`shrink-0 rounded-full px-3.5 py-1.5 text-xs font-bold transition-colors ${
                filter === f.key ? "bg-brand text-white" : "border border-line text-slate-500 hover:border-brand"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {shops === undefined ? (
        <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>
      ) : filtered.length === 0 ? (
        <div className="rounded-2xl border border-line bg-white p-10 text-center">
          <Store className="mx-auto h-10 w-10 text-slate-300" />
          <p className="mt-3 text-sm text-slate-500">Aucune boutique ne correspond à votre recherche.</p>
          <Link to="/devenir-revendeur" className="mt-4 inline-block rounded-full bg-brand px-6 py-3 text-sm font-bold text-white">Devenir revendeur</Link>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((s) => (
            <Link key={s.shop_slug} to={`/boutique-vendeur/${s.shop_slug}`} data-testid={`shop-card-${s.shop_slug}`}
              className={`group relative flex items-center gap-4 rounded-2xl border bg-white p-5 transition-transform duration-200 hover:-translate-y-1 ${
                s.is_premium ? "border-amber-300 shadow-[0_0_0_1px_rgba(251,191,36,0.25)] hover:border-amber-400" : "border-line hover:border-brand"
              }`}>
              {s.is_premium && (
                <span className="absolute right-3 top-3 flex items-center gap-1 rounded-full bg-gradient-to-r from-amber-400 to-orange-500 px-2 py-0.5 text-[10px] font-black text-white shadow">
                  <Crown className="h-3 w-3" /> PREMIUM
                </span>
              )}
              <span className={`flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-xl ${s.is_premium ? "bg-amber-50 text-amber-600" : "bg-brand-light text-brand"}`}>
                {s.shop_logo ? <img src={fileUrl(s.shop_logo)} alt={s.shop_name} className="h-full w-full object-cover" /> : <Store className="h-6 w-6" />}
              </span>
              <div className="min-w-0">
                <p className="truncate font-display text-base font-bold text-ink group-hover:text-brand">{s.shop_name}</p>
                <p className="truncate text-xs text-slate-500">{s.shop_description || s.country}</p>
                <div className="mt-1.5 flex items-center gap-3 text-[11px] font-bold text-slate-400">
                  <span className="flex items-center gap-1"><Package className="h-3 w-3" /> {s.products}</span>
                  <span className="flex items-center gap-1"><GraduationCap className="h-3 w-3" /> {s.formations}</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
